import React from 'react';
import { ArrowRightToLine } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { formatAmount } from '../vip/bonus_requests/format';
import { roundedAmount, roundingFor } from './bonusForm';

const labelClass = 'flex h-[30px] items-center text-xs font-bold uppercase tracking-wider text-ink-dim';

// ModeToggle: type the bonus amount in the account's currency or in USD.
function ModeToggle({ currency, mode, onChange }) {
  const modes = [{ key: 'currency', label: currency || 'Currency' }, { key: 'usd', label: 'USD' }];
  return (
    <div role="group" aria-label="Specify amount in" className="inline-flex rounded-md border border-white/10 bg-surface-1 p-0.5">
      {modes.map((m) => (
        <button
          key={m.key} type="button" aria-pressed={mode === m.key} onClick={() => onChange(m.key)}
          className={cn('rounded px-3 py-1 text-xs font-bold',
            mode === m.key ? 'bg-surface-3 text-ink' : 'text-ink-mute hover:text-ink')}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// usdText: "$1,234.50" (the values are decimal strings with at most 2 decimals)
const usdText = (value) => `$${formatAmount(Number(value).toFixed(2))}`;

// BonusAmountFields: the bonus amount, typed in USD or in the account's currency (the toggle), the
// other value beside it, and in USD mode "Round to …", which rounds the amount up to the
// currency's step from the comps' rounding table (Undo goes back). Items of the form's 12-column
// grid, followed by a full-width hint row with the rate.
export default function BonusAmountFields({ state, accounts, usd, rate, approval, onAmount, onUSD, onMode, onRound, onUndoRound }) {
  const usdMode = state.mode === 'usd';
  const step = roundingFor(accounts, state.currency);
  const preview = roundedAmount(state, accounts);
  const converted = usdMode ? state.amount : usd;
  const warn = approval && 'border-warning/60 bg-warning/10';

  return (
    <>
      <div className="min-w-0 sm:col-span-2 lg:col-span-4">
        <div className="mb-1.5 flex h-[30px] items-center justify-between gap-2">
          <label htmlFor="ib-amount" className={labelClass}>Bonus amount</label>
          <ModeToggle currency={state.currency} mode={state.mode} onChange={onMode} />
        </div>
        <div className="relative">
          {usdMode && (
            <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-sm font-medium text-ink-mute" aria-hidden="true">$</span>
          )}
          <Input
            id="ib-amount" key={state.mode} inputMode="decimal" autoFocus
            value={usdMode ? state.usd : state.amount}
            onChange={(e) => (usdMode ? onUSD : onAmount)(e.target.value)}
            placeholder={usdMode ? '0.00' : '0.00000000'}
            className={cn('h-11 pr-14 font-bold', usdMode ? 'pl-7' : 'pl-3.5', usdMode && warn)}
          />
          <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-xs font-bold text-ink-mute" aria-hidden="true">
            {usdMode ? 'USD' : state.currency}
          </span>
        </div>
      </div>

      <div className="min-w-0 sm:col-span-2 lg:col-span-5">
        <span className={cn(labelClass, 'mb-1.5')}>{usdMode ? `Credited in ${state.currency}` : 'Value in USD'}</span>
        <div className={cn('flex h-11 items-center gap-2.5 rounded-md border border-dashed border-white/15 bg-surface-1 pl-3.5 pr-1.5',
          !usdMode && warn)}
        >
          <span className={cn('text-sm font-bold tabular-nums', converted ? 'text-ink' : 'text-ink-mute', !usdMode && approval && 'text-warning')}>
            {!converted ? '—' : usdMode ? formatAmount(converted, state.currency) : usdText(converted)}
          </span>
          {state.rounded && <span className="rounded-full bg-positive/15 px-2 py-0.5 text-xs font-bold text-positive">Rounded</span>}
          <span className="flex-1" />
          {preview && (
            <button
              type="button" onClick={onRound} title={`Round ${state.currency} up to a multiple of ${step}`}
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded border border-cta/40 bg-cta/10 px-3 text-xs font-bold text-cta hover:bg-cta/20"
            >
              <ArrowRightToLine className="h-3.5 w-3.5" /> Round to {preview}
            </button>
          )}
          {state.rounded && (
            <button
              type="button" onClick={onUndoRound}
              className="h-8 rounded border border-white/15 px-3 text-xs font-bold text-ink-dim hover:text-ink"
            >
              Undo
            </button>
          )}
        </div>
      </div>

      <div className="-mt-1.5 flex flex-wrap gap-x-[18px] gap-y-1.5 text-xs font-medium text-ink-mute sm:col-span-2 lg:col-span-12">
        <span>
          {usdMode
            ? `Amount in USD; ${state.currency} is calculated at today's rate.`
            : `Amount in ${state.currency}; USD shown for reference.`}
          {rate && <> Rate: <code className="font-mono text-ink-dim">{rate}</code></>}
        </span>
        {usdMode && !step && state.currency && <span>No rounding rule for {state.currency}.</span>}
        {state.rounded && (
          <span className="text-ink">
            Player receives <b className="font-bold">{usdText(usd)}</b> (entered {usdText(state.usd)})
          </span>
        )}
      </div>
    </>
  );
}
